import { useState } from 'react'
import { ImageIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { multimodalApi, panelAnnotationSummary, type ScreenplayScene, type StoryboardPanel } from '@/api/multimodalApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { pageItems } from '@/lib/pagination'

const PAGE_SIZE = 12

function EmptyStructuredAsset() {
  const { t } = useTranslation()
  return (
    <div className="rounded-md border border-dashed bg-muted/20 px-3 py-6 text-center text-sm text-muted-foreground">
      {t('detail.pipelineViewer.notGenerated')}
    </div>
  )
}

export function StructuredAssetPagination({
  page,
  pageCount,
  total,
  totalLabelKey = 'pagination.totalImages',
  onPageChange,
}: {
  page: number
  pageCount: number
  total: number
  totalLabelKey?: string
  onPageChange: (page: number) => void
}) {
  const { t } = useTranslation()
  if (pageCount <= 1) return null
  return (
    <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
      <Button type="button" variant="outline" size="sm" disabled={page <= 0} onClick={() => onPageChange(page - 1)}>
        {t('pagination.previous')}
      </Button>
      <span className="text-sm text-muted-foreground">
        {t('pagination.pageWithUnit', { page: page + 1, total: pageCount })}
      </span>
      <span className="text-sm text-muted-foreground">
        {t(totalLabelKey, { total })}
      </span>
      <Button type="button" variant="outline" size="sm" disabled={page >= pageCount - 1} onClick={() => onPageChange(page + 1)}>
        {t('pagination.next')}
      </Button>
    </div>
  )
}

export function ScreenplayView({ scenes }: { scenes: ScreenplayScene[] }) {
  const { t } = useTranslation()
  if (!scenes.length) return <EmptyStructuredAsset />
  return (
    <div className="space-y-3">
      {scenes.slice(0, 4).map((scene, index) => (
        <div key={scene.scene_id || index} className="rounded-md border bg-background/40 p-3">
          <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="outline" className="rounded-md">
              {scene.scene_id || t('detail.pipelineViewer.sceneFallback', { index: index + 1 })}
            </Badge>
            {scene.setting ? <span>{scene.setting}</span> : null}
          </div>
          {scene.action ? <p className="text-sm">{scene.action}</p> : null}
          {scene.narration ? <p className="mt-2 text-sm text-muted-foreground">{scene.narration}</p> : null}
          {scene.dialogues?.length ? (
            <div className="mt-2 space-y-1">
              {scene.dialogues.slice(0, 3).map((dialogue, dialogueIndex) => (
                <p key={`${dialogue.character ?? 'dialogue'}-${dialogueIndex}`} className="text-sm">
                  <span className="font-medium">{dialogue.character || t('detail.pipelineViewer.unknownSpeaker')}</span>
                  <span className="text-muted-foreground">: </span>
                  {dialogue.content}
                </p>
              ))}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  )
}

export function StoryboardView({ panels }: { panels: StoryboardPanel[] }) {
  const { t } = useTranslation()
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({})
  const [page, setPage] = useState(0)
  if (!panels.length) return <EmptyStructuredAsset />
  const paged = pageItems(panels, page, PAGE_SIZE)
  return (
    <div className="space-y-3">
      {/* 🔴 容器查询而非视口断点: xl: 看的是**视口**宽度, 容器只有 422px 时仍按 3 列排,
          实测每格被压到 121.8px, 构图/气泡/旁白全截断。@container 让列数跟容器宽度走。 */}
      <div className="@container">
      <div className="grid gap-3 @md:grid-cols-2 @2xl:grid-cols-3">
        {paged.pageItems.map((panel) => {
          const annotations = panelAnnotationSummary(panel)
          return (
            <div key={panel.id} className="overflow-hidden rounded-md border bg-background/40">
              {failedImages[panel.id] ? (
                <div className="flex aspect-video w-full items-center justify-center bg-muted text-muted-foreground">
                  <ImageIcon className="size-6" />
                </div>
              ) : (
                <img
                  src={multimodalApi.panelImageUrl(panel.id)}
                  alt={t('detail.pipelineViewer.panelImageAlt', { index: panel.panel_sequence })}
                  loading="lazy"
                  onError={() => setFailedImages((current) => ({ ...current, [panel.id]: true }))}
                  className="aspect-video w-full bg-muted object-cover"
                />
              )}
              <div className="space-y-3 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="rounded-md">
                    {t('detail.pipelineViewer.panelNo', { index: panel.panel_sequence })}
                  </Badge>
                  {annotations.shotType ? (
                    <Badge variant="secondary" className="rounded-md">
                      {t('detail.pipelineViewer.shotType')}: {annotations.shotType}
                    </Badge>
                  ) : null}
                </div>
                {annotations.composition ? (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">{t('detail.pipelineViewer.composition')}</p>
                    <p className="line-clamp-3 text-sm">{annotations.composition}</p>
                  </div>
                ) : null}
                {annotations.dialogueBubbles.length ? (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">{t('detail.pipelineViewer.dialogueBubbles')}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {annotations.dialogueBubbles.map((bubble, index) => (
                        <Badge key={`${panel.id}-bubble-${index}`} variant="outline" className="rounded-md text-xs font-normal">
                          {bubble}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ) : null}
                {annotations.narrationBox ? (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">{t('detail.pipelineViewer.narrationBox')}</p>
                    <p className="line-clamp-2 text-sm text-muted-foreground">{annotations.narrationBox}</p>
                  </div>
                ) : null}
              </div>
            </div>
          )
        })}
      </div>
      </div>
      <StructuredAssetPagination page={paged.page} pageCount={paged.pageCount} total={panels.length} onPageChange={setPage} />
    </div>
  )
}
